import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    IconMessage2, IconLoader2, IconRefresh, IconPhoto, IconX,
    IconCircleCheck, IconClockHour4, IconRotateClockwise, IconBan, IconEye,
} from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import printTemplateFeedbackService from '../../../services/printTemplateFeedbackService';
import SelectField from '../../../components/common/SelectField';
import ConfirmDialog from '../../../components/common/ConfirmDialog';

const PAGE_LIMIT = 30;

/**
 * Print-media feedback — the AI negative-feedback screen, over free-form reports.
 *
 * The difference that shapes the table: this feedback is not about a template.
 * There is no template column and no "fix it" action, only who sent it, what
 * they wrote, and whether it has been dealt with.
 */
export default function AdminPrintFeedbackPage() {
    const { t, i18n } = useTranslation();
    const isBn = i18n.language === 'bn';
    const locale = isBn ? 'bn-BD' : 'en-US';
    const fmt = (n) => (n ?? 0).toLocaleString(locale);

    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [preview, setPreview] = useState(null);          // the row being previewed
    const [statusFilter, setStatusFilter] = useState('all');
    const [sortOrder, setSortOrder] = useState('newest');
    const [counts, setCounts] = useState({ all: 0, pending: 0, solved: 0, rejected: 0 });
    const [busyId, setBusyId] = useState(null);
    const [reopenConfirm, setReopenConfirm] = useState(null); // row or null

    const fmtDate = (d) => d ? new Date(d).toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' }) : '—';

    const fetchPage = useCallback(async (pageNum, append) => {
        append ? setLoadingMore(true) : setLoading(true);
        try {
            const params = { page: pageNum, limit: PAGE_LIMIT, sort: sortOrder };
            if (statusFilter !== 'all') params.status = statusFilter;
            const res = await printTemplateFeedbackService.list(params);
            const data = res?.data || {};
            setItems(prev => append ? [...prev, ...(data.items || [])] : (data.items || []));
            setTotal(data.total || 0);
            setHasMore(!!data.hasMore);
            setPage(pageNum);
            if (data.counts) setCounts(data.counts);
        } catch {
            notifications.show({ title: t('printFeedback.error'), message: t('printFeedback.failedToLoad'), color: 'red' });
        } finally {
            append ? setLoadingMore(false) : setLoading(false);
        }
    }, [statusFilter, sortOrder, t]);

    useEffect(() => { fetchPage(1, false); }, [fetchPage]);

    const setStatus = async (row, status) => {
        setBusyId(row.id);
        try {
            const res = await printTemplateFeedbackService.setStatus(row.id, status);
            const updated = res?.data;
            setItems(prev => prev.map(r => (r.id === row.id ? { ...r, ...updated } : r)));
            notifications.show({ title: t('common.success'), message: t('printFeedback.updated'), color: 'green' });
            // The status tabs carry counts, so they have to be re-read.
            fetchPage(1, false);
        } catch (err) {
            notifications.show({ title: t('printFeedback.error'), message: err.message, color: 'red' });
        } finally {
            setBusyId(null);
            setReopenConfirm(null);
        }
    };

    const StatusTab = ({ value, label, count }) => (
        <button
            type="button"
            onClick={() => setStatusFilter(value)}
            className={`inline-flex items-center gap-1.5 h-9 px-3.5 rounded-[11px] border text-[13px] font-semibold transition-colors cursor-pointer ${
                statusFilter === value
                    ? 'border-[#E2702E] bg-[#E2702E] text-white'
                    : 'border-[#E9E6E0] dark:border-zinc-700 bg-white dark:bg-zinc-900 text-[#6E6A63] dark:text-zinc-300 hover:bg-[#FCEFE7] dark:hover:bg-orange-950/30'
            }`}
        >
            {label}
            <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded-full ${
                statusFilter === value ? 'bg-white/25' : 'bg-[#F1EFEA] dark:bg-zinc-800'
            }`}>{fmt(count)}</span>
        </button>
    );

    return (
        <div className="font-hind w-full py-4 md:py-6">
            {/* Header */}
            <div className="mb-4 flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                    <h1 className="text-2xl font-bold tracking-[-0.01em] text-[#1C1B19] dark:text-zinc-100">
                        {t('printFeedback.pageTitle')}
                    </h1>
                    <p className="mt-1 text-sm text-[#6E6A63] dark:text-zinc-400">
                        {t('printFeedback.pageSubtitle')}
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => fetchPage(1, false)}
                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-[11px] border border-[#E9E6E0] dark:border-zinc-700 bg-white dark:bg-zinc-900 text-[13px] font-semibold text-[#6E6A63] dark:text-zinc-300 hover:bg-[#FCEFE7] dark:hover:bg-orange-950/30 transition-colors cursor-pointer"
                >
                    <IconRefresh size={15} className="text-[#E2702E]" /> {t('printFeedback.refresh')}
                </button>
            </div>

            {/* Filters */}
            <div className="flex items-center gap-2 mb-4 flex-wrap">
                <StatusTab value="all" label={t('printFeedback.filterAll')} count={counts.all} />
                <StatusTab value="pending" label={t('printFeedback.statusPending')} count={counts.pending} />
                <StatusTab value="solved" label={t('printFeedback.statusSolved')} count={counts.solved} />
                <StatusTab value="rejected" label={t('printFeedback.statusRejected')} count={counts.rejected} />
                <div className="ml-auto" style={{ flex: '0 0 160px' }}>
                    <SelectField
                        value={sortOrder}
                        onChange={setSortOrder}
                        options={[
                            { value: 'newest', label: t('printFeedback.sortNewest') },
                            { value: 'oldest', label: t('printFeedback.sortOldest') },
                        ]}
                    />
                </div>
            </div>

            {/* Skeleton — mirrors the real table so the swap doesn't jump */}
            {loading && (
                <div className="rounded-2xl border border-[#ECE9E3] dark:border-zinc-700 bg-white dark:bg-zinc-900 overflow-hidden animate-pulse">
                    <div className="h-4 my-2.5 mx-3 bg-[#FAF9F6] dark:bg-zinc-800/60 rounded" />
                    {Array.from({ length: 7 }).map((_, i) => (
                        <div key={i} className="flex items-center gap-4 px-3 py-2 border-t border-[#F1EFEA] dark:border-zinc-800">
                            <div className="w-10 h-10 rounded-lg bg-[#F0EFEC] dark:bg-zinc-800 flex-shrink-0" />
                            <div className="flex-1 min-w-0 space-y-1.5">
                                <div className="h-3 w-3/5 rounded bg-[#F0EFEC] dark:bg-zinc-800" />
                                <div className="h-2.5 w-2/5 rounded bg-[#F0EFEC] dark:bg-zinc-800" />
                            </div>
                            <div className="hidden sm:block h-3 w-32 rounded bg-[#F0EFEC] dark:bg-zinc-800 flex-shrink-0" />
                            <div className="hidden md:block h-5 w-20 rounded-full bg-[#F0EFEC] dark:bg-zinc-800 flex-shrink-0" />
                            <div className="h-8 w-28 rounded-lg bg-[#F0EFEC] dark:bg-zinc-800 flex-shrink-0" />
                        </div>
                    ))}
                </div>
            )}

            {/* Empty */}
            {!loading && items.length === 0 && (
                <div className="flex flex-col items-center gap-3 py-20">
                    <div className="w-16 h-16 rounded-2xl bg-[#F1EFEA] dark:bg-zinc-800 flex items-center justify-center">
                        <IconMessage2 size={28} className="text-[#C3BEB4] dark:text-zinc-600" />
                    </div>
                    <p className="text-sm text-[#6E6A63] dark:text-zinc-400">{t('printFeedback.empty')}</p>
                </div>
            )}

            {/* Table */}
            {!loading && items.length > 0 && (
                <>
                    <p className="text-[13px] text-[#6E6A63] dark:text-zinc-400 mb-3">
                        {t('printFeedback.count', { num: fmt(total) })}
                    </p>
                    <div className="rounded-2xl border border-[#ECE9E3] dark:border-zinc-700 bg-white dark:bg-zinc-900 overflow-hidden overflow-x-auto">
                        <table className="w-full text-left border-collapse min-w-[860px]">
                            <thead>
                                <tr className="bg-[#FAF9F6] dark:bg-zinc-800/60 text-[11px] font-semibold uppercase tracking-wide text-[#9C988F] dark:text-zinc-400">
                                    <th className="px-3 py-2.5 font-semibold">{t('printFeedback.colImage')}</th>
                                    <th className="px-3 py-2.5 font-semibold">{t('printFeedback.colMessage')}</th>
                                    <th className="px-3 py-2.5 font-semibold">{t('printFeedback.colUser')}</th>
                                    <th className="px-3 py-2.5 font-semibold">{t('printFeedback.colDate')}</th>
                                    <th className="px-3 py-2.5 font-semibold">{t('printFeedback.colStatus')}</th>
                                    <th className="px-3 py-2.5 font-semibold text-right">{t('printFeedback.colAction')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((row) => (
                                    <tr key={row.id} className="border-t border-[#F1EFEA] dark:border-zinc-800 hover:bg-[#FAF9F6] dark:hover:bg-zinc-800/40 transition-colors align-middle">
                                        <td className="px-3 py-2">
                                            {row.imagePath ? (
                                                <button
                                                    onClick={() => setPreview(row)}
                                                    title={t('printFeedback.preview')}
                                                    className="block w-10 h-10 rounded-lg overflow-hidden border border-[#ECE9E3] dark:border-zinc-700 bg-[#F0EFEC] dark:bg-zinc-800 cursor-pointer"
                                                >
                                                    <img src={row.imagePath} alt="" loading="lazy" className="w-full h-full object-cover" />
                                                </button>
                                            ) : (
                                                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#F1EFEA] dark:bg-zinc-800 text-[#C3BEB4] dark:text-zinc-600">
                                                    <IconPhoto size={16} />
                                                </div>
                                            )}
                                        </td>
                                        {/* Clamped to three lines — the full text is one click away in the preview. */}
                                        <td className="px-3 py-2 max-w-[420px]">
                                            <p className="text-[13px] text-[#1C1B19] dark:text-zinc-200 whitespace-pre-wrap break-words line-clamp-3">
                                                {row.message}
                                            </p>
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap">
                                            <p className="text-[13px] font-medium text-[#1C1B19] dark:text-zinc-200 truncate max-w-[160px]">{row.user?.name || '—'}</p>
                                            {row.user?.phone && <p className="text-[11px] text-[#9C988F] dark:text-zinc-500">{row.user.phone}</p>}
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap text-[12.5px] text-[#6E6A63] dark:text-zinc-400">
                                            {fmtDate(row.createdAt)}
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap">
                                            {row.status === 'rejected' ? (
                                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-red-500/10 text-red-500 dark:text-red-400">
                                                    <IconBan size={13} /> {t('printFeedback.statusRejected')}
                                                </span>
                                            ) : row.status === 'solved' ? (
                                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                                                    <IconCircleCheck size={13} /> {t('printFeedback.statusSolved')}
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-amber-500/10 text-amber-600 dark:text-amber-400">
                                                    <IconClockHour4 size={13} /> {t('printFeedback.statusPending')}
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-right whitespace-nowrap">
                                            <div className="inline-flex items-center gap-1.5">
                                                <button
                                                    onClick={() => setPreview(row)}
                                                    title={t('printFeedback.preview')}
                                                    className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg border border-[#E9E6E0] dark:border-zinc-700 text-[12.5px] font-semibold text-[#6E6A63] dark:text-zinc-300 hover:bg-[#FAF9F6] dark:hover:bg-zinc-800 transition-colors cursor-pointer"
                                                >
                                                    <IconEye size={13} /> {t('printFeedback.preview')}
                                                </button>
                                                {row.status === 'pending' ? (
                                                    <>
                                                        <button
                                                            onClick={() => setStatus(row, 'rejected')}
                                                            disabled={busyId === row.id}
                                                            className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg border border-red-200 dark:border-red-900/40 text-[12.5px] font-semibold text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors cursor-pointer disabled:opacity-50"
                                                        >
                                                            {busyId === row.id ? <IconLoader2 size={13} className="animate-spin" /> : <IconBan size={13} />}
                                                            {t('printFeedback.reject')}
                                                        </button>
                                                        <button
                                                            onClick={() => setStatus(row, 'solved')}
                                                            disabled={busyId === row.id}
                                                            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-[#E2702E] hover:bg-[#E2702E]/90 text-white text-[12.5px] font-semibold transition-colors cursor-pointer disabled:opacity-50"
                                                        >
                                                            <IconCircleCheck size={14} /> {t('printFeedback.resolve')}
                                                        </button>
                                                    </>
                                                ) : (
                                                    <button
                                                        onClick={() => setReopenConfirm(row)}
                                                        disabled={busyId === row.id}
                                                        className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg border border-[#E9E6E0] dark:border-zinc-700 text-[12.5px] font-semibold text-[#6E6A63] dark:text-zinc-300 hover:bg-[#FAF9F6] dark:hover:bg-zinc-800 transition-colors cursor-pointer disabled:opacity-50"
                                                    >
                                                        {busyId === row.id ? <IconLoader2 size={13} className="animate-spin" /> : <IconRotateClockwise size={13} />}
                                                        {t('printFeedback.reopen')}
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {hasMore && (
                        <div className="flex justify-center mt-5">
                            <button
                                onClick={() => fetchPage(page + 1, true)}
                                disabled={loadingMore}
                                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-[10px] border border-[#E9E6E0] dark:border-zinc-700 bg-white dark:bg-zinc-900 text-[13px] font-semibold text-[#6E6A63] dark:text-zinc-300 hover:bg-[#FCEFE7] dark:hover:bg-orange-950/30 transition-colors cursor-pointer disabled:opacity-50"
                            >
                                {loadingMore && <IconLoader2 size={16} className="animate-spin text-[#E2702E]" />}
                                {t('printFeedback.loadMore')}
                            </button>
                        </div>
                    )}
                </>
            )}

            <ConfirmDialog
                open={!!reopenConfirm}
                onClose={() => setReopenConfirm(null)}
                onConfirm={() => setStatus(reopenConfirm, 'pending')}
                loading={busyId != null}
                variant="primary"
                title={t('printFeedback.reopenConfirmTitle')}
                description={t('printFeedback.reopenConfirmDesc')}
                confirmLabel={t('printFeedback.reopen')}
            />

            {/* Preview: the screenshot and the full message together — the row
                clamps the text, so this is where it is actually readable. */}
            {preview && (
                <div
                    className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/80 p-4"
                    onClick={() => setPreview(null)}
                >
                    <button
                        onClick={() => setPreview(null)}
                        aria-label={t('common.close')}
                        className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center transition-colors z-10 cursor-pointer"
                    >
                        <IconX size={20} />
                    </button>
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white dark:bg-zinc-900 p-5 flex flex-col gap-4"
                    >
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <p className="text-[15px] font-bold text-[#1C1B19] dark:text-zinc-100 truncate">
                                    {preview.user?.name || '—'}
                                </p>
                                {preview.user?.phone && (
                                    <p className="text-[12.5px] text-[#9C988F] dark:text-zinc-500">{preview.user.phone}</p>
                                )}
                            </div>
                            <p className="text-[12px] text-[#9C988F] dark:text-zinc-500 whitespace-nowrap">
                                {fmtDate(preview.createdAt)}
                            </p>
                        </div>

                        {preview.imagePath ? (
                            <img
                                src={preview.imagePath}
                                alt=""
                                className="w-full max-h-[52vh] rounded-lg object-contain bg-[#F0EFEC] dark:bg-zinc-800"
                            />
                        ) : (
                            <div className="rounded-lg border border-dashed border-[#E9E6E0] dark:border-zinc-700 py-8 flex flex-col items-center gap-2 text-[#C3BEB4] dark:text-zinc-600">
                                <IconPhoto size={24} />
                                <span className="text-[12.5px]">{t('printFeedback.noImage')}</span>
                            </div>
                        )}

                        <p className="text-[14px] leading-relaxed text-[#1C1B19] dark:text-zinc-200 whitespace-pre-wrap break-words">
                            {preview.message}
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
}
